import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { fetchContent, subscribeToType, LIVE_MODE } from '../lib/sanity'
import { FaHeartbeat, FaBalanceScale, FaBrain } from 'react-icons/fa'
import { IconType } from 'react-icons'

interface InfoCardData {
  title: string
  description: string
  iconType: string
}

interface GLP1InfoContent {
  heading: string
  description: string
  infoCards: InfoCardData[]
  disclaimer: string
}

const iconMap: Record<string, IconType> = {
  heartbeat: FaHeartbeat,
  balance: FaBalanceScale,
  brain: FaBrain,
}

const InfoCard = ({ icon: Icon, title, description, delay }: {
  icon: IconType
  title: string
  description: string
  delay: number
}) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    viewport={{ once: true }}
    className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white/90 p-6 shadow-[0_15px_40px_rgba(15,23,42,0.08)]"
  >
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-teal-50 via-white to-teal-100" />
    <div className="relative space-y-4">
      <div className="inline-flex items-center justify-center rounded-2xl bg-teal-50 p-4 text-teal-600 shadow-inner">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-2xl font-semibold text-slate-900">{title}</h3>
      <p className="text-base text-slate-600 leading-relaxed">{description}</p>
    </div>
  </motion.div>
)

const GLP1Info = () => {
  const [content, setContent] = useState<GLP1InfoContent | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    const getContent = async () => {
      try {
        const [data] = await fetchContent('glp1Info')
        setContent(data)
        if (LIVE_MODE) {
          unsubscribe = subscribeToType('glp1Info', (docs) => setContent(docs[0]))
        }
      } catch (error) {
        console.error('Error fetching GLP1 info content:', error)
      }
    }
    getContent()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const cards = content?.infoCards || [
    {
      title: 'Build a steadier routine',
      description: 'Use simple check-ins and repeatable habits to create more consistency around the routines that matter to you.',
      iconType: 'heartbeat',
    },
    {
      title: 'Make room for lifestyle change',
      description: 'Create practical routines around meals, movement, rest, and the daily choices that support your longer-term goals.',
      iconType: 'balance',
    },
    {
      title: 'Stay accountable to yourself',
      description: 'Have a supportive guide for planning, reflection, and consistency while your licensed care team handles clinical questions.',
      iconType: 'brain',
    },
  ]

  return (
    <section id="glp1-info-section" className="relative isolate overflow-hidden bg-[#cdfbf1] py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(205,251,241,0.6)_0%,rgba(255,255,255,0.8)_100%)]" />

      <div className="container relative z-10 mx-auto px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-teal-500">Practical insight</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-semibold text-slate-900">
            {content?.heading || 'Building routines that support your goals'}
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            {content?.description || 'Andrea provides non-medical accountability and lifestyle support for people using GLP-1 medication alongside licensed clinical care.'}
          </p>
        </div>

        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, index) => (
            <InfoCard
              key={`${card.title}-${index}`}
              icon={iconMap[card.iconType]}
              title={card.title}
              description={card.description}
              delay={index * 0.12}
            />
          ))}
        </div>

        <div className="mt-12 rounded-3xl border border-teal-200/70 bg-white/80 p-6 text-sm text-slate-600 shadow-inner">
          {content?.disclaimer || 'Andrea is not a licensed healthcare provider. Discuss medication, symptoms, and treatment decisions with your licensed clinician.'}
        </div>
      </div>
    </section>
  )
}

export default GLP1Info
